import { Alert, Badge } from 'react-bootstrap';
import { CONTRACT_DISPLAY, contractBadge, resolveContract } from '@/config/contract';
import type { ClientConfig, DeployBlock, InactiveTag } from '@/types';
import { asSentence, deployBlockTitle, inactiveTitle, setByLine, type FlagReader } from '@/utils/clientFlags';
import './clientFlags.css';

/** The small red tag beside a client that must not be deployed to. The reason shows on hover. */
export function DoNotDeployTag({ block }: { block: DeployBlock }) {
  return (
    <Badge bg="danger" className="client-tag" title={deployBlockTitle(block)}>
      Do not deploy
    </Badge>
  );
}

/** The muted tag beside an inactive client. Who set it, and why, show on hover. */
export function InactiveTagBadge({ tag }: { tag: InactiveTag }) {
  return (
    <Badge bg="secondary" className="client-tag" title={inactiveTitle(tag)}>
      Inactive
    </Badge>
  );
}

/**
 * The client's contract: "Credits", "Partner: Ohnyx", "Trial, expired". The
 * contract's meaning shows on hover.
 */
export function ContractTag({ label, variant, hint }: { label: string; variant: string; hint?: string }) {
  // The strong fills (primary, dark) are muted so the contract never reads louder than a warning tag.
  const quiet = variant === 'primary' || variant === 'dark' ? 'secondary' : variant;
  return (
    <Badge bg={quiet} className="client-tag" title={hint}>
      {label}
    </Badge>
  );
}

interface ClientTagsProps {
  clientName: string;
  /** The tags, from `useClientFlags`. A list passes its one copy to every row. */
  flags: FlagReader;
  /**
   * The client's config, when the caller holds it. A client with no contract
   * on the portal's record then shows the billing in its config.
   */
  config?: Pick<ClientConfig, 'contractType'>;
  /** Leave one of the tags out where the view already says it another way. */
  hide?: 'inactive' | 'deploy-block' | 'contract' | ReadonlyArray<'inactive' | 'deploy-block' | 'contract'>;
  /** The tags sit on the solid fill of a chosen row. */
  onSelected?: boolean;
}

/** Whichever tags a client carries, side by side: its contract, do not deploy, inactive. Nothing when it carries none. */
export function ClientTags({ clientName, flags, config, hide, onSelected = false }: ClientTagsProps) {
  const hidden = new Set(typeof hide === 'string' ? [hide] : (hide ?? []));
  const block = hidden.has('deploy-block') ? undefined : flags.deployBlock(clientName);
  const inactive = hidden.has('inactive') ? undefined : flags.inactive(clientName);
  const metadata = flags.row(clientName);
  const contract = hidden.has('contract') ? null : contractBadge(metadata, config);
  const resolved = resolveContract(metadata, config);
  if (!block && !inactive && !contract) return null;
  return (
    <span className={`client-tags${onSelected ? ' client-tags--on-selected' : ''}`}>
      {contract && <ContractTag {...contract} hint={resolved ? CONTRACT_DISPLAY[resolved].hint : undefined} />}
      {block && <DoNotDeployTag block={block} />}
      {inactive && <InactiveTagBadge tag={inactive} />}
    </span>
  );
}

/**
 * The warning shown wherever a person is about to deploy to a blocked client:
 * do not deploy, the reason, and who set it.
 */
export function DeployBlockNotice({
  clientName,
  block,
  className,
}: {
  clientName: string;
  block: DeployBlock;
  className?: string;
}) {
  return (
    <Alert variant="danger" className={['client-block-notice', className].filter(Boolean).join(' ')}>
      <span className="client-block-notice__lead">Do not deploy to {clientName}.</span> {asSentence(block.reason)}{' '}
      <span className="client-block-notice__who">{setByLine(block)}.</span>
    </Alert>
  );
}
